import { API_URL } from '../../../../constants/urls.constants';
import { appServiceApi } from '../../../apis/app-service.api';
import { APP_API_TAGS } from '../../../app.constants';
import { invalidateOnSuccess } from '../../../app.utils';
import type {
  AdminKPIPlanDetailResponse,
  AdminKPIPlanListResponse,
  AdminKPIPlanParams,
  ApproveTeamKPIRequest,
  ApproveBulkKPIRequest,
  ReturnTeamKPIRequest,
  CreateOwnKPIRequest,
  DraftOwnKPIRequest,
  DraftTeamKPIRequest,
  OwnKPIDetailResponse,
  OwnKPIListResponse,
  OwnKPIParams,
  PerformanceLogResponse,
  TeamKPIListResponse,
  TeamKPIParams,
  TeamKPIDetailResponse,
  DraftOwnAppraisalRequest,
  CreateOwnAppraisalRequest,
  OwnAppraisalParams,
  OwnAppraisalListResponse,
  OwnAppraisalDetailResponse,
  DraftTeamAppraisalRequest,
  CreateTeamAppraisalRequest,
  TeamAppraisalParams,
  TeamAppraisalListResponse,
  TeamAppraisalDetailResponse,
  AdminKPIListResponse,
  AdminKPIParams,
  AdminKPIDetailResponse,
  AdminAppraisalListResponse,
  AdminAppraisalParams,
  AdminAppraisalDetailResponse,
  DashboardGraphsParams,
  DashboardGraphsResponse,
} from './performance.type';

const { PERFORMANCE } = APP_API_TAGS;

export const performanceApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET: All Admin KPI Plans
    getAllAdminKPIPlan: builder.query<
      AdminKPIPlanListResponse,
      AdminKPIPlanParams
    >({
      query: (params) => ({
        url: API_URL.performanceAdminPlans,
        method: 'GET',
        params,
      }),
      providesTags: [PERFORMANCE],
    }),

    // GET: Admin KPI Plan Details
    getAdminKPIPlanDetails: builder.query<AdminKPIPlanDetailResponse, string>({
      query: (id) => ({
        url: `${API_URL.performanceAdminPlan}/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: PERFORMANCE as any, id }],
    }),

    // POST: Create Admin KPI Plan
    createAdminKPIPlan: builder.mutation<any, any>({
      query: (body) => ({
        url: API_URL.performanceAdminPlan,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // PATCH: Update Admin KPI Plan
    updateAdminKPIPlan: builder.mutation<any, { id: string; body: any }>({
      query: ({ body }) => ({
        url: `${API_URL.performanceAdminPlan}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([
        PERFORMANCE,
        { type: PERFORMANCE as any, id: true },
      ]),
    }),

    // DELETE: Delete Admin KPI Plan
    deleteAdminKPIPlan: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `${API_URL.performanceAdminPlan}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // ──────── Employee KPI ────────

    // PATCH: Draft Own KPI
    draftOwnKPI: builder.mutation<any, DraftOwnKPIRequest>({
      query: (body) => ({
        url: API_URL.performanceEmployeeDraftKPI,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // PATCH: Create (Submit) Own KPI
    createOwnKPI: builder.mutation<any, CreateOwnKPIRequest>({
      query: (body) => ({
        url: API_URL.performanceEmployeeSubmitKPI,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // GET: All Own KPIs
    getAllOwnKPI: builder.query<OwnKPIListResponse, OwnKPIParams>({
      query: (params) => ({
        url: API_URL.performanceEmployeeKPIs,
        method: 'GET',
        params,
      }),
      providesTags: [PERFORMANCE],
    }),

    // GET: Own KPI Details
    getOwnKPIDetails: builder.query<OwnKPIDetailResponse, string>({
      query: (id) => ({
        url: `${API_URL.performanceEmployeeKPI}/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: PERFORMANCE as any, id }],
    }),

    // GET: Performance Log View
    getLogData: builder.query<PerformanceLogResponse, string>({
      query: (id) => ({
        url: `${API_URL.performanceLogView}/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: PERFORMANCE as any, id }],
    }),

    // ──────── Employee Appraisal ────────

    // PATCH: Draft Own Appraisal
    draftOwnAppraisal: builder.mutation<any, DraftOwnAppraisalRequest>({
      query: (body) => ({
        url: API_URL.performanceEmployeeDraftAppraisal,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // PATCH: Create (Submit) Own Appraisal
    createOwnAppraisal: builder.mutation<any, CreateOwnAppraisalRequest>({
      query: (body) => ({
        url: API_URL.performanceEmployeeSubmitAppraisal,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // GET: All Own Appraisals
    getAllOwnAppraisal: builder.query<OwnAppraisalListResponse, OwnAppraisalParams>({
      query: (params) => ({
        url: API_URL.performanceEmployeeAppraisals,
        method: 'GET',
        params,
      }),
      providesTags: [PERFORMANCE],
    }),

    // GET: Own Appraisal Details
    getOwnAppraisalDetails: builder.query<OwnAppraisalDetailResponse, string>({
      query: (id) => ({
        url: `${API_URL.performanceEmployeeAppraisal}/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: PERFORMANCE as any, id }],
    }),

    // ──────── Manager KPI ────────

    // GET: All Team KPIs
    getAllTeamKPI: builder.query<TeamKPIListResponse, TeamKPIParams>({
      query: (params) => ({
        url: API_URL.performanceManagerKPIs,
        method: 'GET',
        params,
      }),
      providesTags: [PERFORMANCE],
    }),

    // GET: Team KPI Details
    getTeamKPIDetails: builder.query<TeamKPIDetailResponse, string>({
      query: (id) => ({
        url: `${API_URL.performanceManagerKPI}/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: PERFORMANCE as any, id }],
    }),

    // PATCH: Draft Team KPI
    draftTeamKPI: builder.mutation<any, DraftTeamKPIRequest>({
      query: (body) => ({
        url: API_URL.performanceManagerDraftKPI,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // PATCH: Approve (Submit) Team KPI
    approveTeamKPI: builder.mutation<any, ApproveTeamKPIRequest>({
      query: (body) => ({
        url: API_URL.performanceManagerSubmitKPI,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // PATCH: Return Team KPI
    returnTeamKPI: builder.mutation<any, ReturnTeamKPIRequest>({
      query: (body) => ({
        url: API_URL.performanceManagerReturnKPI,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // POST: Bulk Approve Team KPI
    approveBulkKPI: builder.mutation<any, ApproveBulkKPIRequest>({
      query: (body) => ({
        url: API_URL.performanceManagerBulkApproveKPI,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // ──────── Manager Appraisal ────────

    // PATCH: Draft Team Appraisal
    draftTeamAppraisal: builder.mutation<any, DraftTeamAppraisalRequest>({
      query: (body) => ({
        url: API_URL.performanceManagerDraftAppraisal,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // PATCH: Create (Submit) Team Appraisal
    createTeamAppraisal: builder.mutation<any, CreateTeamAppraisalRequest>({
      query: (body) => ({
        url: API_URL.performanceManagerSubmitAppraisal,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // GET: All Team Appraisals
    getAllTeamAppraisal: builder.query<
      TeamAppraisalListResponse,
      TeamAppraisalParams
    >({
      query: (params) => ({
        url: API_URL.performanceManagerAppraisals,
        method: 'GET',
        params,
      }),
      providesTags: [PERFORMANCE],
    }),

    // GET: Team Appraisal Details
    getTeamAppraisalDetails: builder.query<TeamAppraisalDetailResponse, string>({
      query: (id) => ({
        url: `${API_URL.performanceManagerAppraisal}/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: PERFORMANCE as any, id }],
    }),

    // ──────── Admin Appraisal ────────

    // PATCH: Draft Admin Appraisal
    draftAdminAppraisal: builder.mutation<any, CreateTeamAppraisalRequest>({
      query: (body) => ({
        url: API_URL.performanceAdminDraftAppraisal,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    draftAppraisalByadmin: builder.mutation<any, CreateTeamAppraisalRequest>({
      query: (body) => ({
        url: API_URL.performanceAdminDraftAppraisal,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // PATCH: Approve Admin Appraisal
    approveAdminAppraisal: builder.mutation<any, CreateTeamAppraisalRequest>({
      query: (body) => ({
        url: API_URL.performanceAdminSubmitAppraisal,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    approveAppraisalByAdmin: builder.mutation<any, CreateTeamAppraisalRequest>({
      query: (body) => ({
        url: API_URL.performanceAdminSubmitAppraisal,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // PATCH: Create KPI Appraisal Due Date
    createKPIAppraisal: builder.mutation<any, any>({
      query: (body) => ({
        url: API_URL.performanceAdminAppraisalDueDate,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([PERFORMANCE]),
    }),

    // GET: All Admin Appraisals
    getAllAdminAppraisal: builder.query<
      AdminAppraisalListResponse,
      AdminAppraisalParams
    >({
      query: (params) => ({
        url: API_URL.performanceAdminAppraisals,
        params,
      }),
      providesTags: [PERFORMANCE],
    }),

    // GET: Admin Appraisal Details
    getAdminAppraisalDetails: builder.query<AdminAppraisalDetailResponse, string>({
      query: (id) => ({
        url: `${API_URL.performanceAdminAppraisal}/${id}`,
      }),
      providesTags: [PERFORMANCE],
    }),

    // ──────── Admin KPI ────────

    // GET: All Admin KPIs
    getAllEmployeeKPIByAdmin: builder.query<
      AdminKPIListResponse,
      AdminKPIParams
    >({
      query: (params) => ({
        url: API_URL.performanceAdminKPIs,
        params,
      }),
      providesTags: [PERFORMANCE],
    }),

    // GET: Admin KPI Details
    getEmployeeKPIDetailsByAdmin: builder.query<AdminKPIDetailResponse, string>({
      query: (id) => ({
        url: `${API_URL.performanceAdminKPI}/${id}`,
      }),
      providesTags: [PERFORMANCE],
    }),

    // GET: Admin Performance Dashboard Graphs
    getDashboardGraphs: builder.query<DashboardGraphsResponse, DashboardGraphsParams>({
      query: (params) => ({
        url: API_URL.performanceAdminDashboardGraphs,
        method: 'GET',
        params,
      }),
      providesTags: [PERFORMANCE],
    }),
  }),
});

export const {
  useGetAllAdminKPIPlanQuery,
  useGetAdminKPIPlanDetailsQuery,
  useCreateAdminKPIPlanMutation,
  useUpdateAdminKPIPlanMutation,
  useDeleteAdminKPIPlanMutation,
  // Employee hooks
  useDraftOwnKPIMutation,
  useCreateOwnKPIMutation,
  useGetAllOwnKPIQuery,
  useGetOwnKPIDetailsQuery,
  useGetLogDataQuery,
  // Employee Appraisal hooks
  useDraftOwnAppraisalMutation,
  useCreateOwnAppraisalMutation,
  useGetAllOwnAppraisalQuery,
  useGetOwnAppraisalDetailsQuery,
  // Manager hooks
  useGetAllTeamKPIQuery,
  useGetTeamKPIDetailsQuery,
  useDraftTeamKPIMutation,
  useApproveTeamKPIMutation,
  useReturnTeamKPIMutation,
  useApproveBulkKPIMutation,
   // Manager Appraisal hooks
  useDraftTeamAppraisalMutation,
  useCreateTeamAppraisalMutation,
  useGetAllTeamAppraisalQuery,
  useGetTeamAppraisalDetailsQuery,
  // Admin Appraisal hooks
  useDraftAdminAppraisalMutation,
  useApproveAdminAppraisalMutation,
  useDraftAppraisalByadminMutation,
  useApproveAppraisalByAdminMutation,
  useGetAllAdminAppraisalQuery,
  useGetAdminAppraisalDetailsQuery,
  useCreateKPIAppraisalMutation,
  // Admin KPI hooks
  useGetAllEmployeeKPIByAdminQuery,
  useGetEmployeeKPIDetailsByAdminQuery,
  useGetDashboardGraphsQuery,
} = performanceApi;
